import type {
  Citizenship,
  CurrencyCode,
  LenderAssessment,
  LenderProse,
} from '../types';

export interface LenderProseInput {
  citizenship: Citizenship;
  currency: CurrencyCode;
}

interface ProsCons {
  pros: string[];
  cons: string[];
}

const STATIC_PROFILE: Record<string, ProsCons> = {
  anz: {
    pros: [
      'Major-bank rate and product stability.',
      'DTI cap of 8x — supports higher-leverage borrowers.',
      'Standard variable investment product with full feature set.',
    ],
    cons: [
      'Does not lend to foreign nationals.',
      'Non-AUD income shaded at 80% for base salary.',
    ],
  },
  cba: {
    pros: [
      'Largest AU lender — competitive on serviceability.',
      'DSCR-based assessment rather than hard DTI cap.',
      'Investment Simple Variable product with flexibility.',
    ],
    cons: [
      'Bonus shading drops to 64% for most non-AUD currencies.',
      'No lending to foreign nationals.',
    ],
  },
  bankwest: {
    pros: [
      'Sharper rate than the majors (≈5.84% p.a. PI).',
      'DSCR-based serviceability supports higher leverage.',
    ],
    cons: [
      'Lower base-salary shade for some currencies (down to 70%).',
      'Higher floor rate (5.4%) than ANZ.',
      'Credit-card factor applied at 0.456/12 — more conservative.',
    ],
  },
  ma_money: {
    pros: [
      'Highest LVR ceiling — 90% for AU citizens/PR.',
      'No currency shading — 100% income recognition.',
      'Lends to foreign nationals up to 70% LVR.',
    ],
    cons: [
      'Specialist lender — higher rate (≈7.09% p.a. PI).',
      'Floor rate at 5.75% lifts assessment-rate threshold.',
    ],
  },
};

function tailorByProfile(
  base: ProsCons,
  lender: LenderAssessment,
  input: LenderProseInput,
): ProsCons {
  const pros = [...base.pros];
  const cons = [...base.cons];

  if (
    lender.verdict === 'fail' &&
    lender.maxLvr === 0 &&
    input.citizenship === 'foreign_national'
  ) {
    cons.unshift('Not available for foreign-national borrowers.');
  }
  if (lender.verdict === 'amber' && lender.surplus < 0) {
    cons.unshift('Negative serviceability surplus — borderline.');
  }
  if (lender.verdict === 'pass' && lender.surplus >= 3000) {
    pros.unshift('Strong surplus on this lender’s servicing model.');
  }

  return { pros, cons };
}

export function computeLenderProse(
  lenders: LenderAssessment[],
  input: LenderProseInput,
): LenderProse[] {
  return lenders.map((l) => {
    const base = STATIC_PROFILE[l.name] ?? {
      pros: ['Specialist lender — review with broker.'],
      cons: ['Limited public information on this product.'],
    };
    const tailored = tailorByProfile(base, l, input);
    return {
      lenderName: l.name,
      pros: tailored.pros,
      cons: tailored.cons,
    };
  });
}
